// ============================================================================
// SCHEDULE DELETION MODAL
// Set, change or cancel a content deletion date for a session or project.
// The backend job deletes from 00:00 UK time on that date and warns 7 days
// and 1 day before. Shows the certificate once the delete has run.
// ============================================================================

import React, { useEffect, useState } from 'react';
import { X, CalendarClock, Download, AlertCircle, FileText } from 'lucide-react';
import {
  DeletionTargetType,
  useDeletionSchedule,
  useSetDeletionSchedule,
  useCancelDeletionSchedule,
  downloadDeletionCertificate,
  BreakGlassContext,
} from '../../hooks/useProjects';

interface ScheduleDeletionModalProps {
  isOpen: boolean;
  onClose: () => void;
  targetType: DeletionTargetType;
  targetId: string;
  targetName: string;
  /** A system admin acting on a project they do not own (Project::Delete needs break-glass) */
  breakGlass?: BreakGlassContext | null;
}

/** Tomorrow's date in UK time, the earliest a deletion can be scheduled */
function ukTomorrow(): string {
  const today = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/London' }).format(new Date());
  const d = new Date(`${today}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString().slice(0, 10);
}

export function formatDeleteOn(ymd: string): string {
  return new Intl.DateTimeFormat('en-GB', { dateStyle: 'long', timeZone: 'UTC' }).format(new Date(`${ymd}T00:00:00Z`));
}

export function daysUntilDeleteOn(ymd: string): number {
  const today = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/London' }).format(new Date());
  return Math.round((Date.parse(`${ymd}T00:00:00Z`) - Date.parse(`${today}T00:00:00Z`)) / 86_400_000);
}

export const ScheduleDeletionModal: React.FC<ScheduleDeletionModalProps> = ({
  isOpen, onClose, targetType, targetId, targetName, breakGlass,
}) => {
  const { data, isLoading } = useDeletionSchedule(targetType, isOpen ? targetId : null);
  const setSchedule = useSetDeletionSchedule(targetType, targetId, breakGlass);
  const cancelSchedule = useCancelDeletionSchedule(targetType, targetId, breakGlass);
  const [deleteOn, setDeleteOn] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const schedule = data?.schedule ?? null;
  const certificate = data?.certificate ?? null;

  useEffect(() => {
    if (!isOpen) return;
    setDeleteOn(schedule?.deleteOn ?? '');
    setReason(schedule?.reason ?? '');
    setError(null);
  }, [isOpen, schedule?.deleteOn, schedule?.reason]);

  if (!isOpen) return null;

  const busy = setSchedule.isPending || cancelSchedule.isPending;
  const scope = targetType === 'project' ? 'project, including every session in it' : 'session';

  const save = () => {
    setError(null);
    setSchedule.mutate({ deleteOn, reason: reason.trim() || undefined }, { onError: (e) => setError(e.message) });
  };
  const cancel = () => {
    setError(null);
    cancelSchedule.mutate(undefined, { onError: (e) => setError(e.message) });
  };
  const download = () => {
    downloadDeletionCertificate(targetType, targetId).catch((e) => setError(e.message));
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-5" onClick={() => !busy && onClose()}>
      <div className="w-full max-w-[520px] overflow-hidden rounded-2xl bg-white shadow-xl font-display" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-[#e2e8f0] px-6 py-5">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-50 text-red-600">
              <CalendarClock size={22} />
            </div>
            <h2 className="text-lg font-bold tracking-tight text-navy">Schedule content deletion</h2>
          </div>
          <button onClick={onClose} disabled={busy} className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600">
            <X size={20} />
          </button>
        </div>

        {error && (
          <div className="flex gap-3 border-b border-[#e2e8f0] bg-red-50 px-5 py-4">
            <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-500" />
            <p className="text-sm font-medium text-red-700">{error}</p>
          </div>
        )}

        <div className="flex flex-col gap-4 p-5 text-sm text-slate-600">
          <p>
            On the chosen date (from midnight UK time), all content in the {scope} <strong className="text-navy">{targetName}</strong> is
            permanently deleted and a deletion certificate is kept. People with write access are warned 7 days and 1 day before.
          </p>

          {isLoading && <p className="text-slate-400">Loading…</p>}

          {certificate && (
            <div className="rounded-xl border border-green-200 bg-green-50 p-3 text-green-800">
              Content deleted on {new Date(certificate.completedAt).toLocaleString('en-GB')} (scheduled for {formatDeleteOn(certificate.deleteOn)}).
              <button
                onClick={download}
                className="mt-2 flex items-center justify-center gap-2 rounded-xl h-9 px-4 border border-slate-200 bg-white text-slate-600 text-sm font-bold hover:bg-slate-50 shadow-sm transition-colors"
              >
                <Download size={16} /> Download certificate
              </button>
            </div>
          )}

          {schedule && (
            <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-red-800">
              Scheduled for <strong>{formatDeleteOn(schedule.deleteOn)}</strong> ({daysUntilDeleteOn(schedule.deleteOn)} days) by{' '}
              {schedule.requestedByEmail || schedule.requestedBy}.
              {schedule.attempts > 0 && (
                <p className="mt-1.5 text-red-700">
                  Deletion has run {schedule.attempts} time{schedule.attempts === 1 ? '' : 's'} without completing and retries hourly: {schedule.lastError}
                </p>
              )}
            </div>
          )}

          <div>
            <label htmlFor="delete-on" className="block text-sm font-semibold text-navy mb-2 flex items-center gap-1.5">
              <CalendarClock className="w-4 h-4 text-slate-400" />Delete on
            </label>
            <input
              id="delete-on" type="date" min={ukTomorrow()} value={deleteOn} onChange={(e) => setDeleteOn(e.target.value)}
              className="w-full border border-[#e2e8f0] rounded-xl px-3 py-2.5 text-sm text-navy focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
            />
          </div>
          <div>
            <label htmlFor="delete-reason" className="block text-sm font-semibold text-navy mb-2 flex items-center gap-1.5">
              <FileText className="w-4 h-4 text-slate-400" />Reason
            </label>
            <input
              id="delete-reason" type="text" maxLength={500} value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Customer request"
              className="w-full border border-[#e2e8f0] rounded-xl px-3 py-2.5 text-sm text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
            />
            <p className="mt-2 text-xs text-slate-400">Shown on the deletion certificate.</p>
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 border-t border-[#e2e8f0] bg-slate-50/50 px-5 py-4">
          <div>
            {schedule && (
              <button
                onClick={cancel}
                disabled={busy}
                className="flex items-center justify-center gap-2 rounded-xl h-10 px-4 border border-slate-200 bg-white text-red-600 text-sm font-bold hover:bg-red-50 shadow-sm transition-colors disabled:opacity-60"
              >
                Cancel schedule
              </button>
            )}
          </div>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              disabled={busy}
              className="flex items-center justify-center gap-2 rounded-xl h-10 px-4 border border-slate-200 bg-white text-slate-600 text-sm font-bold hover:bg-slate-50 shadow-sm transition-colors"
            >
              Close
            </button>
            <button
              onClick={save}
              disabled={busy || !deleteOn || (deleteOn === schedule?.deleteOn && reason === (schedule?.reason ?? ''))}
              className="bg-red-600 text-white font-medium text-sm py-2 px-4 rounded-lg hover:bg-red-700 disabled:opacity-60"
            >
              {schedule ? 'Update schedule' : 'Schedule deletion'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
